import { type MouseEventHandler, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { css, type Theme } from '@emotion/react';
import { ArrowSquareOut, CornersIn, CornersOut, TerminalWindow } from '@phosphor-icons/react';
import { adminJobs } from 'common/deployment/JobDocuments';
import { StatusItemState } from 'common/status/StatusDocument';
import { useSocket } from 'client/lib/Socket';
import { useUser } from 'client/lib/User';
import { isAdminLog, isContainerLog } from 'client/state/LogState';
import { Button } from 'client/components/Button';
import { DialogContent } from 'client/components/Dialog/DialogContent';
import { useLog } from 'client/components/Log/useLog';
import { Status } from 'client/components/Status/Status';
import { LogConsole } from './LogConsole';

const logStateToStatus = {
  loading: {
    itemStateID: StatusItemState.GRAY,
    isPending: true,
  },
  error: {
    itemStateID: StatusItemState.RED,
    noBlink: true,
  },
  ok: {
    itemStateID: StatusItemState.GREEN,
  },
};

export interface LogViewProps {
  deploymentID: number | null;
  //  sidebarLogs: (ClientLogViewJob & { icon?: 'cube' | 'gear' })[];
  onLogSelect: (index: number) => void;
}

// Type used for rendering the log page including sidebar items
export type LogViewItem = {
  deploymentID: number;
  url: string;
  type: 'container' | 'exec' | 'admin';
  name: string;
  namespace: string;
  command: string[];
};

export const LogView = ({
  isDialog,
  fullscreen,
  onToggleFullscreen,
}: {
  isDialog: boolean;
  fullscreen?: boolean;
  onToggleFullscreen?: MouseEventHandler<HTMLButtonElement>;
}) => {
  const { hasAccessRight } = useUser();

  const { activeLog, closeLogDialog, appendLogData } = useLog();

  const fixStatus = 'loading';
  const status = useMemo(() => (fixStatus ? <Status size={16} {...logStateToStatus[fixStatus]} /> : null), [fixStatus]);

  if (!activeLog) {
    // TODO: Handle deployment loading, errors here...
    return <div>Fixme</div>;
  }

  const { settings, jobUUID } = activeLog;
  const { title } = settings;

  const breadcrumbs: React.ReactNode[] = [];

  if (isContainerLog(settings)) {
    const { deployment, componentName, pod, container } = settings;
    const { projectName, deploymentName } = deployment;
    breadcrumbs.push(<span key="p">{projectName} </span>);
    breadcrumbs.push(<span key="d">{deploymentName} </span>);
    breadcrumbs.push(<span key="c">{componentName} </span>);
    if (pod) breadcrumbs.push(<span key="p2">{pod} </span>);
    if (container) breadcrumbs.push(<span key="c2">{container} </span>);
  } else if (isAdminLog(settings)) {
    const { deployment } = settings;
    const { projectName, deploymentName } = deployment!;
    const job = adminJobs.find((j) => j.identifier == settings.adminLogIdentifier);
    //TODO: Check job here
    breadcrumbs.push(<span key="p">{projectName} </span>);
    breadcrumbs.push(<span key="d">{deploymentName} </span>);
    breadcrumbs.push(<span key="c"> {job!.name} </span>);
  } else {
    throw new Error('log type not implemented');
  }

  // This will forward incoming log messages to client log storage
  const handleLogStream = useCallback(({ jobUUID, data }: { jobUUID: string; data: string }) => {
    appendLogData({ jobUUID, data });
  }, []);
  useSocket({ listeners: [{ path: '/deployment/job/stream', handler: handleLogStream }] });

  const handleClose = () => closeLogDialog();
  const handleOpenInNewTab = () => closeLogDialog();

  return (
    <DialogContent
      title={title}
      description={
        <span>
          {breadcrumbs.flatMap((b, i) => (i < breadcrumbs.length - 1 ? [b, <span key={`sep-${i}`}> / </span>] : [b]))}
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        </span>
      }
      icon={<TerminalWindow />}
      dismissable={isDialog}
      actions={null}
      headerActions={
        isDialog ? (
          <>
            <Link target="_blank" to={activeLog?.settings.url || ''}>
              <Button
                tooltip="Open in new tab"
                tooltipSide="bottom"
                icon={<ArrowSquareOut size={24} weight="fill" />}
                onClick={handleOpenInNewTab}
              />
            </Link>
            <Button
              icon={fullscreen ? <CornersIn size={24} weight="bold" /> : <CornersOut size={24} weight="bold" />}
              tooltip={fullscreen ? 'Exit full screen' : 'Full screen'}
              tooltipSide="bottom"
              onClick={onToggleFullscreen}
            />
          </>
        ) : null
      }
      onClose={handleClose}
      css={contentStyle}
    >
      <div css={logStyle}>
        <LogConsole jobUUID={jobUUID || null} />
      </div>
    </DialogContent>
  );
};

const contentStyle = (theme: Theme) => css`
  display: flex;
  height: 100%;
  background-color: ${theme.color.black};
  overflow: hidden;
`;

const logStyle = (theme: Theme) => css`
  height: 100%;

  > div {
    height: 100%;

    > div {
      background-color: black;
      scrollbar-color: ${theme.color.grey[80]} black;
    }

    #log {
      padding: 0;
      height: 100%;
    }
  }
`;
